import React, { useState, useEffect, useMemo } from "react";
import "./TrainingLearning.scss";
import { Breadcrumb } from "components";
import { t } from "react-switch-lang";
import api from "components/api/api";
import {
  Search,
  Filter,
  Plus,
  X,
  Upload,
  Eye,
  Users,
  Calendar,
  PlayCircle,
  FileText,
  CheckCircle,
} from "lucide-react";
import { Edit, Trash2 } from "react-feather";

const getUploadConfig = (file) => {
  const ext = file.name.split(".").pop().toLowerCase();

  if (["mp4", "webm", "mov"].includes(ext)) {
    return { resourceType: "video", uploadType: "video" };
  }
  if (ext === "pdf") {
    return { resourceType: "image", uploadType: "image" };
  }
  if (["doc", "docx", "ppt", "pptx", "xls", "xlsx"].includes(ext)) {
    return { resourceType: "raw", uploadType: "raw" };
  }

  throw new Error("Unsupported file type");
};

const uploadToCloudinary = async (file, onProgress) => {
  const { uploadType } = getUploadConfig(file);

  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", "training_videos");

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve(JSON.parse(xhr.responseText));
      } else {
        reject(xhr.responseText);
      }
    };

    xhr.onerror = () => reject("Upload failed");

    xhr.open(
      "POST",
      `https://api.cloudinary.com/v1_1/dxv3dts8k/${uploadType}/upload`
    );

    xhr.send(formData);
  });
};

const TrainingLearning = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [maxWatchedTime, setMaxWatchedTime] = useState({});
  const [materialsLoading, setMaterialsLoading] = useState(true);
  const [editingMaterial, setEditingMaterial] = useState(null);

  const userRole = (localStorage.getItem("userRole") || "").toLowerCase();
  const userId = localStorage.getItem("userId") || "";

  // Training materials state
  const [trainingMaterials, setTrainingMaterials] = useState([]);
  const [videoEngagement, setVideoEngagement] = useState([]);

  // Upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [trainingTitle, setTrainingTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [cloudinaryData, setCloudinaryData] = useState(null);

  // Region/User state
  const [regionData, setRegionData] = useState({});
  const [regionNames, setRegionNames] = useState([]);
  const [assignSelectedRegion, setAssignSelectedRegion] =
    useState("All Regions");
  const [selectedDistrict, setSelectedDistrict] = useState("All Outlet");
  const [districts, setDistricts] = useState([]);
  const [userList, setUserList] = useState([]);
  const [selectedLearners, setSelectedLearners] = useState([]);
  const [assignSearchQuery, setAssignSearchQuery] = useState("");

  // Video viewing state (for non-admin users)
  const [viewingVideo, setViewingVideo] = useState(null);
  const [currentProgress, setCurrentProgress] = useState({});
  const [lastSavedProgress, setLastSavedProgress] = useState({});

  const [error, setError] = useState(null);

  const categories = ["all", "video", "document"];
  const isAdmin = userRole === "admin";

  // Progress milestones to trigger API calls
  const PROGRESS_MILESTONES = [10, 25, 50, 75, 90, 100];

  // Fetch training materials
  const fetchTrainingMaterials = async () => {
    setMaterialsLoading(true);
    try {
      const response = await api.fetchAllTrainings();
      setTrainingMaterials(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch training materials:", error);
      setError("Failed to load training materials");
    } finally {
      setMaterialsLoading(false);
    }
  };

  const handleSeeking = (e, materialId) => {
    const video = e.target;
    const allowedTime = maxWatchedTime[materialId] || 0;

    // Allow rewind but NOT forward jump
    if (video.currentTime > allowedTime + 1) {
      video.currentTime = allowedTime;
    }
  };

  const fetchUserTrainingMaterials = async () => {
    if (!userId) return;

    setMaterialsLoading(true);
    try {
      const res = await api.fetchUserTrainings(userId);

      const progressMap = {};

      const mappedMaterials = (res.data.data || []).map((item) => {
        progressMap[item.trainingId] = item.progress || 0;

        return {
          id: item.trainingId,
          title: item.title,
          cloudinaryUrl: item.cloudinaryUrl,
          type: item.type,
          duration: item.duration,
          dueDate: item.dueDate,
          region: "assigned",
          uploadDate: item.createdAt,
        };
      });

      setTrainingMaterials(mappedMaterials);
      setCurrentProgress(progressMap);
      setLastSavedProgress(progressMap);
    } catch (error) {
      console.error("Failed to fetch user trainings:", error);
      setError("Failed to load assigned trainings");
    } finally {
      setMaterialsLoading(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      if (isAdmin) {
        await fetchTrainingMaterials();
        await fetchLearners();
        await fetchVideoEngagement();
      } else {
        await fetchUserTrainingMaterials();
      }
    };

    initialize();
  }, [isAdmin, userId]);

  const handleDeleteTraining = async (materialId) => {
    if (!window.confirm("Are you sure you want to delete this training?"))
      return;

    try {
      await api.deleteTraining(materialId);
      if (isAdmin) {
        await fetchTrainingMaterials();
      } else {
        await fetchUserTrainingMaterials();
      }
      alert("Training deleted successfully");
    } catch (err) {
      console.error("Delete failed", err);
      alert("Failed to delete training");
    }
  };

  const handleEditTraining = (material) => {
    setEditingMaterial(material);
    setTrainingTitle(material.title);
    setDueDate(
      material.dueDate
        ? new Date(material.dueDate).toISOString().split("T")[0]
        : ""
    );
    setAssignSelectedRegion(
      material.region === "all" ? "All Regions" : material.region
    );
    setShowUploadModal(true);
  };

  // Fetch learners
  const fetchLearners = async () => {
    try {
      const res = await api.fetchUsers();
      const users = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data?.users)
        ? res.data.users
        : [];

      const filteredUsers = users.filter((u) => u.role !== "ADMIN");
      const regionMap = { "All Regions": {} };

      filteredUsers.forEach((user) => {
        const { id, username, region, outlet } = user;
        if (!region || !outlet) return;

        if (!regionMap[region]) regionMap[region] = {};
        if (!regionMap[region][outlet]) regionMap[region][outlet] = [];

        regionMap[region][outlet].push({
          id,
          name: username,
          region,
          outlet,
        });
      });

      setRegionData(regionMap);
      setRegionNames(Object.keys(regionMap));
    } catch (err) {
      console.error("Failed to fetch learners", err);
    }
  };

  // Fetch video engagement
  const fetchVideoEngagement = async () => {
    try {
      const res = await api.fetchVideoEngagement();
      setVideoEngagement(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error) {
      console.error("Failed to fetch video engagement:", error);
    }
  };

  // Initialize users when regionData loads
  useEffect(() => {
    if (Object.keys(regionData).length === 0) return;

    const allUsers = Object.values(regionData)
      .flatMap((r) => Object.values(r || {}))
      .flat();

    setUserList(allUsers);
    setSelectedLearners(allUsers.map((u) => u.id));
  }, [regionData]);

  // Set default due date (7 days from now)
  useEffect(() => {
    const defaultDue = new Date();
    defaultDue.setDate(defaultDue.getDate() + 7);
    setDueDate(defaultDue.toISOString().split("T")[0]);
  }, []);

  const handleRegionChange = (region) => {
    setAssignSelectedRegion(region);

    if (region === "All Regions") {
      const allDistricts = Object.values(regionData).flatMap((r) =>
        Object.keys(r || {})
      );
      const allUsers = Object.values(regionData)
        .flatMap((r) => Object.values(r || {}))
        .flat();

      setDistricts(allDistricts);
      setSelectedDistrict("All Outlet");
      setUserList(allUsers);
      setSelectedLearners(allUsers.map((u) => u.id));
    } else {
      const regionDistricts = Object.keys(regionData[region] || {});
      const usersInRegion = Object.values(regionData[region] || {}).flat();

      setDistricts(regionDistricts);
      setSelectedDistrict("All Outlet");
      setUserList(usersInRegion);
      setSelectedLearners(usersInRegion.map((u) => u.id));
    }
  };

  const getMaterialProgress = (materialId) => {
    // For non-admin users, return their own progress
    if (!isAdmin) {
      return currentProgress[materialId] || 0;
    }

    // For admin, return average progress
    const engagements = videoEngagement.filter(
      (e) => e.trainingId === materialId
    );

    if (engagements.length === 0) return 0;

    const avg =
      engagements.reduce((sum, e) => sum + (e.progress || 0), 0) /
      engagements.length;

    return Math.round(avg);
  };

  const handleDistrictChange = (district) => {
    setSelectedDistrict(district);

    if (district === "All Outlet") {
      const users =
        assignSelectedRegion === "All Regions"
          ? Object.values(regionData)
              .flatMap((r) => Object.values(r || {}))
              .flat()
          : Object.values(regionData[assignSelectedRegion] || {}).flat();

      setUserList(users);
      setSelectedLearners(users.map((u) => u.id));
    } else {
      const users =
        assignSelectedRegion === "All Regions"
          ? Object.values(regionData)
              .flatMap((r) => r[district] || [])
              .flat()
          : regionData[assignSelectedRegion]?.[district] || [];

      setUserList(users);
      setSelectedLearners(users.map((u) => u.id));
    }
  };

  const calculateDuration = (seconds) => {
    if (!seconds || seconds <= 0) return "0 min";
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (mins === 0) return `${secs} sec`;
    return secs > 0 ? `${mins} min ${secs} sec` : `${mins} min`;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("File size exceeds 500MB limit");
      return;
    }

    setUploadedFile(file);

    // Auto-upload to Cloudinary
    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const response = await uploadToCloudinary(file, setUploadProgress);
      setCloudinaryData(response);
    } catch (error) {
      console.error("Upload failed:", error);
      setError("Failed to upload file");
      alert("Failed to upload file");
      setUploadedFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!trainingTitle.trim()) return alert("Enter title");
    if (!editingMaterial && !cloudinaryData) return alert("Upload a file");
    if (selectedLearners.length === 0) return alert("Select learners");

    setLoading(true);

    try {
      const isVideo =
        cloudinaryData?.resource_type === "video" ||
        editingMaterial?.type === "video";

      const payload = {
        title: trainingTitle,
        dueDate: new Date(dueDate).toISOString(),
        region:
          assignSelectedRegion === "All Regions"
            ? "all"
            : assignSelectedRegion.toLowerCase(),
        userIds: selectedLearners,
        ...(cloudinaryData && {
          cloudinaryUrl: cloudinaryData.secure_url,
          cloudinaryFormat: cloudinaryData.format,
          cloudinaryResourceType: cloudinaryData.resource_type,
          cloudinaryPublicId: cloudinaryData.public_id,
          type: isVideo ? "video" : "document",
          duration: isVideo
            ? calculateDuration(cloudinaryData.duration)
            : "N/A",
        }),
      };

      if (editingMaterial) {
        await api.updateTraining(editingMaterial.id, payload);
        alert("Training updated successfully");
      } else {
        await api.uploadTraining({
          material: payload,
          userIds: selectedLearners,
          dueDate: payload.dueDate,
        });
        alert("Training uploaded successfully");
      }
      if (isAdmin) {
        await fetchTrainingMaterials();
      } else {
        await fetchUserTrainingMaterials();
      }

      resetModal();
      setEditingMaterial(null);
    } catch (err) {
      console.error(err);
      alert("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleLearnerSelection = (learnerId) => {
    setSelectedLearners((prev) => {
      if (prev.includes(learnerId)) {
        return prev.filter((id) => id !== learnerId);
      }
      return [...prev, learnerId];
    });
  };

  // Update progress in backend (only at milestones)
  const updateProgressInBackend = async (trainingId, progress) => {
    try {
      await api.updateTrainingProgress({
        userId,
        trainingId,
        progress,
        status:
          progress >= 100
            ? "completed"
            : progress > 0
            ? "in-progress"
            : "not-started",
      });

      console.log(`Progress saved at ${progress}% for training ${trainingId}`);

      // Update last saved progress
      setLastSavedProgress((prev) => ({
        ...prev,
        [trainingId]: progress,
      }));
    } catch (error) {
      console.error("Failed to update progress:", error);
    }
  };

  // Check if progress has crossed a milestone
  const shouldUpdateProgress = (trainingId, newProgress) => {
    const lastProgress = lastSavedProgress[trainingId] || 0;

    // ❌ Never update if progress goes backward or stays same
    if (newProgress <= lastProgress) return false;

    return PROGRESS_MILESTONES.some(
      (m) => lastProgress < m && newProgress >= m
    );
  };

  // Handle video time update
  const handleVideoTimeUpdate = (e, materialId) => {
    const video = e.target;
    if (!video || !video.duration) return;

    setMaxWatchedTime((prev) => {
      const prevMax = prev[materialId] || 0;

      // Only increase if watched sequentially
      if (video.currentTime > prevMax) {
        return {
          ...prev,
          [materialId]: video.currentTime,
        };
      }
      return prev;
    });

    const watchedTime = maxWatchedTime[materialId] || video.currentTime;

    const progress = Math.min(
      Math.floor((watchedTime / video.duration) * 100),
      100
    );

    // ❌ Do NOT allow progress to decrease
    setCurrentProgress((prev) => ({
      ...prev,
      [materialId]: Math.max(prev[materialId] || 0, progress),
    }));

    if (shouldUpdateProgress(materialId, progress)) {
      updateProgressInBackend(materialId, progress);
    }
  };

  // View material with video tracking for non-admin users
  const viewMaterial = (material) => {
    if (!material.cloudinaryUrl) {
      alert("File URL not available");
      return;
    }

    // For non-admin users viewing videos, open in modal to track progress
    if (!isAdmin && material.type === "video") {
      setViewingVideo(material);
      return;
    }

    // Otherwise open in new tab
    window.open(material.cloudinaryUrl, "_blank", "noopener,noreferrer");
  };

  // Close video modal
  const closeVideoModal = () => {
    setViewingVideo(null);
  };

  const resetModal = () => {
    setShowUploadModal(false);
    setTrainingTitle("");
    setUploadedFile(null);
    setUploadProgress(0);
    setCloudinaryData(null);
    setSelectedLearners([]);
    setAssignSelectedRegion("All Regions");
    setSelectedDistrict("All Outlet");
    setError(null);
    setIsUploading(false);
  };

  // Filtered materials
  const filteredMaterials = useMemo(() => {
    return trainingMaterials.filter((material) => {
      const matchesSearch =
        material.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        material.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        selectedCategory === "all" || material.type === selectedCategory;
      const matchesRegion =
        selectedRegion === "all" ||
        material.region === selectedRegion ||
        material.region === "all";

      return matchesSearch && matchesCategory && matchesRegion;
    });
  }, [searchQuery, selectedCategory, selectedRegion, trainingMaterials]);

  // Filtered users for assign
  const filteredUserList = useMemo(() => {
    if (!assignSearchQuery.trim()) return userList;
    return userList.filter((user) =>
      user.name.toLowerCase().includes(assignSearchQuery.toLowerCase())
    );
  }, [userList, assignSearchQuery]);

  // Calculate stats
  const getStats = () => {
    const totalMaterials = trainingMaterials.length;
    const totalAssignments = trainingMaterials.reduce(
      (sum, m) => sum + (m.assignedTo || 0),
      0
    );
    const completedVideos = videoEngagement.filter(
      (e) => e.status === "completed"
    ).length;
    const avgCompletion = Math.round(
      videoEngagement.reduce((sum, e) => sum + e.progress, 0) /
        (videoEngagement.length || 1)
    );
    return { totalMaterials, totalAssignments, completedVideos, avgCompletion };
  };

  const stats = getStats();

  if (materialsLoading) {
    return (
      <div className="training-learning">
        <Breadcrumb title={t("Training & Learning")} />
        <div style={{ textAlign: "center", padding: "3rem" }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="training-learning offers-container">
      <Breadcrumb title={t("Training & Learning")} />

      <div className="offers-header">
        <div className="header-content">
          <div className="header-title-row">
            <div>
              <p className="page-subtitle">
                {isAdmin
                  ? "Upload, Assign & Track Learning Materials"
                  : "Access your training materials and track progress"}
              </p>
            </div>
            {isAdmin && (
              <button
                className="create-btn"
                onClick={() => setShowUploadModal(true)}
              >
                <Plus size={20} />
                Upload
              </button>
            )}
          </div>
        </div>

        <div className="header-stats">
          <div className="stat-card">
            <span className="stat-value">{stats.totalMaterials}</span>
            <span className="stat-label">Training Materials</span>
          </div>
          {isAdmin && (
            <>
              <div className="stat-card">
                <span className="stat-value">{stats.totalAssignments}</span>
                <span className="stat-label">Total Assignments</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{stats.completedVideos}</span>
                <span className="stat-label">Completed</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{stats.avgCompletion}%</span>
                <span className="stat-label">Avg Completion</span>
              </div>
            </>
          )}
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: "1rem",
            margin: "1rem 0",
            backgroundColor: "#fee",
            color: "#c33",
            borderRadius: "4px",
          }}
        >
          {error}
        </div>
      )}

      <div className="filters-section">
        <div className="search-box">
          <Search className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Search training materials..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <div className="filter-label">
            <Filter size={16} />
            <span>Type</span>
          </div>
          <div className="filter-buttons">
            {categories.map((category) => (
              <button
                key={category}
                className={`filter-btn ${
                  selectedCategory === category ? "active" : ""
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {category === "all" ? "All" : category}
              </button>
            ))}
          </div>
        </div>
        {isAdmin && (
          <div className="filter-group">
            <div className="filter-label">
              <Filter size={16} />
              <span>Region</span>
            </div>
            <div className="filter-buttons">
              <button
                className={`filter-btn ${
                  selectedRegion === "all" ? "active" : ""
                }`}
                onClick={() => setSelectedRegion("all")}
              >
                All Regions
              </button>
              {regionNames
                .filter((r) => r !== "All Regions")
                .map((region) => (
                  <button
                    key={region}
                    className={`filter-btn ${
                      selectedRegion === region ? "active" : ""
                    }`}
                    onClick={() => setSelectedRegion(region)}
                  >
                    {region}
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>

      <div className="offers-grid">
        {filteredMaterials.length > 0 ? (
          filteredMaterials.map((material) => (
            <div key={material.id} className="offer-card">
              <div className="offer-header-badge">
                <div className="category-badge">
                  {material.type === "video" ? (
                    <PlayCircle size={18} />
                  ) : (
                    <FileText size={18} />
                  )}
                  <span>{material.type}</span>
                </div>
                <div className="region-badge">{material.region}</div>
              </div>

              <h3 className="offer-title">{material.title}</h3>
              {material.description && (
                <p className="offer-description">{material.description}</p>
              )}

              {isAdmin ? (
                <div className="admin-meta">
                  <div className="meta-item">
                    <Users size={14} />
                    <span>{material.assignedTo || 0} assigned</span>
                  </div>
                  <div className="meta-item">
                    <span>{material.completionRate || 0}% completed</span>
                  </div>
                </div>
              ) : (
                // Progress bar for regular users (video only)
                material.type === "video" && (
                  <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "0.5rem",
                        fontSize: "0.85rem",
                        color: "#7f8c8d",
                      }}
                    >
                      <span>Progress</span>
                      <span style={{ fontWeight: "600", color: "#2c3e50" }}>
                        {getMaterialProgress(material.id)}%
                      </span>
                    </div>
                    <div
                      style={{
                        height: "8px",
                        backgroundColor: "#e9ecef",
                        borderRadius: "10px",
                        overflow: "hidden",
                        boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${getMaterialProgress(material.id)}%`,
                          backgroundColor:
                            getMaterialProgress(material.id) >= 100
                              ? "#2ecc71"
                              : getMaterialProgress(material.id) >= 50
                              ? "#3498db"
                              : "#f39c12",
                          borderRadius: "10px",
                          transition:
                            "width 0.3s ease, background-color 0.3s ease",
                          boxShadow: "0 0 10px rgba(52, 152, 219, 0.5)",
                        }}
                      />
                    </div>
                  </div>
                )
              )}

              <div className="offer-footer">
                <div className="offer-meta">
                  <Calendar size={14} />
                  <span>
                    {material.uploadDate
                      ? new Date(material.uploadDate).toLocaleDateString()
                      : "N/A"}
                  </span>
                </div>
                {material.type === "video" && (
                  <div className="valid-until">⏱️ {material.duration}</div>
                )}
              </div>

              <button
                className="view-details-btn"
                onClick={() => viewMaterial(material)}
              >
                <Eye size={18} />
                View Material
              </button>

              {isAdmin && (
                <div className="admin-actions">
                  <button
                    className="icon-btn edit"
                    onClick={() => handleEditTraining(material)}
                    title="Edit"
                  >
                    <Edit size={16} />
                  </button>

                  <button
                    className="icon-btn delete"
                    onClick={() => handleDeleteTraining(material.id)}
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="no-results">
            <Search size={48} />
            <h3>No training materials found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Video Viewing Modal for Non-Admin Users */}
      {viewingVideo && !isAdmin && (
        <div className="modal-overlay" onClick={closeVideoModal}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "1000px" }}
          >
            <div className="modal-header-offer">
              <h2>{viewingVideo.title}</h2>
              <button className="close-btn" onClick={closeVideoModal}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              {/* Video Player */}

              <video
                src={viewingVideo.cloudinaryUrl}
                controls
                autoPlay
                onTimeUpdate={(e) => handleVideoTimeUpdate(e, viewingVideo.id)}
                onSeeking={(e) => handleSeeking(e, viewingVideo.id)}
                style={{
                  width: "100%",
                  maxHeight: "70vh",
                  borderRadius: "8px",
                  backgroundColor: "#000",
                }}
              />

              {/* Completion Message */}
              {currentProgress[viewingVideo.id] >= 100 && (
                <div
                  style={{
                    marginTop: "1rem",
                    padding: "1rem",
                    backgroundColor: "#d4edda",
                    border: "1px solid #c3e6cb",
                    borderRadius: "8px",
                    color: "#155724",
                    textAlign: "center",
                    fontWeight: "600",
                  }}
                >
                  🎉 Congratulations! You've completed this training video!
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Single Step Upload & Assign Modal */}
      {showUploadModal && isAdmin && (
        <div className="modal-overlay" onClick={resetModal}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "800px" }}
          >
            <div className="modal-header-offer">
              <h2>Upload & Assign Training</h2>
              <button className="close-btn" onClick={resetModal}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              {/* Training Details Section */}
              <div className="form-section">
                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Training Title *</label>
                    <input
                      type="text"
                      value={trainingTitle}
                      onChange={(e) => setTrainingTitle(e.target.value)}
                      placeholder="Enter training title"
                    />
                  </div>
                </div>

                <div className="upload-area" style={{ marginTop: "1rem" }}>
                  <div className="upload-icon">
                    {cloudinaryData ? (
                      <CheckCircle size={48} color="#2ecc71" />
                    ) : (
                      "📁"
                    )}
                  </div>
                  <p>
                    {uploadedFile
                      ? `Selected: ${uploadedFile.name}`
                      : "Drag & drop files here or click to browse"}
                  </p>
                  {cloudinaryData && (
                    <p
                      style={{
                        color: "#2ecc71",
                        fontWeight: "600",
                        marginTop: "0.5rem",
                      }}
                    >
                      ✓ File uploaded successfully
                    </p>
                  )}
                  <p
                    style={{
                      fontSize: "0.85rem",
                      color: "#7f8c8d",
                      marginTop: "0.5rem",
                    }}
                  >
                    Supported: Videos (MP4, WebM, MOV), Documents (PDF, DOC,
                    PPT, XLS)
                  </p>
                  <label
                    className="btn-primary"
                    style={{
                      cursor: isUploading ? "not-allowed" : "pointer",
                      marginTop: "1rem",
                    }}
                  >
                    <Upload size={18} />
                    {isUploading ? "Uploading..." : "Select File"}
                    <input
                      type="file"
                      style={{ display: "none" }}
                      onChange={handleFileSelect}
                      accept="video/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                      disabled={isUploading}
                    />
                  </label>
                  {uploadProgress > 0 && isUploading && (
                    <div className="progress-bar" style={{ marginTop: "1rem" }}>
                      <div
                        className="progress-fill"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                      <span className="progress-text">{uploadProgress}%</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group" style={{ marginTop: "1rem" }}>
                <label>Due Date *</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>

              {/* Assignment Section */}
              <div className="form-section" style={{ marginTop: "1rem" }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Region *</label>
                    <select
                      value={assignSelectedRegion}
                      onChange={(e) => handleRegionChange(e.target.value)}
                    >
                      {regionNames.map((region) => (
                        <option key={region} value={region}>
                          {region}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Outlet</label>
                    <select
                      value={selectedDistrict}
                      onChange={(e) => handleDistrictChange(e.target.value)}
                      disabled={assignSelectedRegion === "All Regions"}
                    >
                      <option value="All Outlet">All Outlet</option>
                      {districts.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Search Learners</label>
                  <div className="search-box">
                    <Search className="search-icon" size={20} />
                    <input
                      type="text"
                      placeholder="Search learners..."
                      value={assignSearchQuery}
                      onChange={(e) => setAssignSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <label style={{ cursor: "pointer", fontWeight: 600 }}>
                      <input
                        type="checkbox"
                        checked={
                          filteredUserList.length > 0 &&
                          selectedLearners.length === filteredUserList.length
                        }
                        onChange={() =>
                          setSelectedLearners(
                            selectedLearners.length === filteredUserList.length
                              ? []
                              : filteredUserList.map((u) => u.id)
                          )
                        }
                      />
                      Select All ({filteredUserList.length})
                    </label>
                    <span
                      style={{
                        color: "#2ecc71",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                      }}
                    >
                      {selectedLearners.length} selected
                    </span>
                  </div>

                  <div
                    className="user-list"
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      border: "1px solid #ecf0f1",
                      borderRadius: "8px",
                      padding: "0.5rem",
                    }}
                  >
                    {filteredUserList.map((user) => (
                      <label
                        key={user.id}
                        className="user-checkbox"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "0.75rem",
                          borderRadius: "6px",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#f8f9fa")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <input
                          type="checkbox"
                          checked={selectedLearners.includes(user.id)}
                          onChange={() => toggleLearnerSelection(user.id)}
                          style={{ marginRight: "0.75rem" }}
                        />
                        <div>
                          <div style={{ fontWeight: "500" }}>{user.name}</div>
                          <div
                            style={{ fontSize: "0.85rem", color: "#7f8c8d" }}
                          >
                            {user.outlet} • {user.region}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="modal-footer" style={{ marginTop: "2rem" }}>
                <button
                  className="cancel-btn"
                  onClick={resetModal}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="save-btn"
                  onClick={handleSubmit}
                  disabled={
                    !trainingTitle.trim() ||
                    !cloudinaryData ||
                    selectedLearners.length === 0 ||
                    !dueDate ||
                    loading ||
                    isUploading
                  }
                  style={{
                    opacity:
                      !trainingTitle.trim() ||
                      !cloudinaryData ||
                      selectedLearners.length === 0 ||
                      !dueDate ||
                      loading ||
                      isUploading
                        ? 0.6
                        : 1,
                    backgroundColor: "#2ecc71",
                  }}
                >
                  <CheckCircle size={18} />
                  {loading
                    ? "Submitting..."
                    : `Upload & Assign to ${selectedLearners.length} Learner(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingLearning;
